// @arch:pure-engine
// composeSubmission.js — G3-WS1-T02 (refactor-deepening-r2 / G3, composer engine).
//
// Pure, framework-free engine owning the SessionComposer's SUBMISSION logic that was
// inline (and only reachable through the heavy component): the "is there anything to
// send?" invariant (2 sites: the submit guard + the hasContent flag), the assembly of
// the outgoing text from the typed draft + the invoked-skill pills + the design-element
// pills, the dedup rules for those pill lists, and the @mention-token removal splice.
//
// Deletion test (01-DOCTRINE): delete this and the hasContent check scatters across its
// 2 call sites, and the skillPrefix/designPrompt/baseValue assembly + the pill-dedup +
// the index-drift-guarded mention splice scatter back into the god-component. The
// interface (these small pure functions) is the test surface — no React/DOM needed.
//
// PURE: no react, no window, no document. Testable by its interface.

/**
 * Is there anything to send? True when ANY of the draft parts is present (text is
 * trimmed). The submit guard is `!composerHasContent(...)` → return; the toolbar's
 * hasContent flag is this exact value.
 * @param {{ text?: string, attachments?: any[], designEls?: any[], invokedSkills?: any[] }} parts
 * @returns {boolean}
 */
export function composerHasContent({ text = '', attachments = [], designEls = [], invokedSkills = [] } = {}) {
  return !!(
    String(text ?? '').trim() ||
    (attachments?.length ?? 0) > 0 ||
    (designEls?.length ?? 0) > 0 ||
    (invokedSkills?.length ?? 0) > 0
  );
}

/**
 * Assemble the outgoing text: invoked-skill commands (front, space-joined) + design
 * element prompts (front, concatenated) + the trimmed draft. Returns the pieces the
 * caller needs (value + skillPrefix for the /save detection, baseValue for the turn).
 * @param {{ text?: string, invokedSkills?: any[], designEls?: any[] }} parts
 * @returns {{ value: string, skillPrefix: string, baseValue: string }}
 */
export function buildSubmissionText({ text = '', invokedSkills = [], designEls = [] } = {}) {
  const value = String(text ?? '').trim();
  const skillPrefix = (invokedSkills || []).map((c) => c.slash_command).join(' ');
  const designPrompt = (designEls || []).map((d) => d.prompt).join('');
  const baseValue = `${skillPrefix ? `${skillPrefix} ` : ''}${designPrompt}${value}`;
  return { value, skillPrefix, baseValue };
}

/** Append an invoked skill, deduped by slash_command (an already-invoked skill is a no-op). */
export function addInvokedSkill(list, cmd) {
  return (list || []).some((c) => c.slash_command === cmd.slash_command) ? list : [...(list || []), cmd];
}

/** Append a design element, deduped by prompt (clicking the same element twice is a no-op). */
export function addDesignElement(list, el) {
  return (list || []).some((d) => d.prompt === el.prompt) ? list : [...(list || []), el];
}

/**
 * Splice a pending @mention's raw token out of the text. Guards against index drift
 * (rapid edits): if the [start,end) slice still matches `raw` it splices by index, else
 * it strips the first occurrence of `raw`. Trailing double-spaces are collapsed.
 * @param {string} text
 * @param {{ start: number, end: number, raw: string }} m
 * @returns {string}
 */
export function removeMentionToken(text, m) {
  if (typeof text !== 'string' || !m) return text;
  const next =
    text.slice(m.start, m.end) === m.raw
      ? text.slice(0, m.start) + text.slice(m.end)
      : text.replace(m.raw, '');
  return next.replace(/ {2,}/g, ' ');
}
